import { AdminCallCardStatusType } from "components/ChatCard/AdminCallCard/AdminCallCard";
import { CustomField } from "components/ChatCard/AIQuestionCard/customField.types";
import qs from "qs";
import {
  DefaultStreamChatGenerics,
  MessageUIComponentProps,
} from "stream-chat-react";
import { emptyApi } from "./emptyApi";
import { MessageResponse } from "stream-chat";
import {
  EmailSuppressionStatus,
  MessageEmailDetailsType,
} from "types/Models/Chat";
import { fileAndFolder } from "types/Models/documents";
import { THREAD_FEEDBACK_RATING } from "constants/threadChat";
import { CALL_SUMMARY_STATUS, CallSummaryStatus } from "constants/callSummary";
import { triggerChannelListRefetch } from "store/slices/chat";
type Result = {
  member_id: string;
  member_name: string;
  member_image_url: string;
  member_assigned_status: boolean;
};
export type AgentDetails = {
  called_agent: {
    name: string;
    email: string;
    profile_id: string;
  };
  company_group_name: string;
  company_group_user_name: string;
  company_group_user_phone: string;
  company_group_uuid: string;
  created_at: string;
  uuid: string;
  call_type: string;
  company_group_user_map_id: string;
  time: string;
};

export type AIAgentReplyCard = {
  header: string;
  metadata: { fields: CustomField[] };
};

export type AIAgentReply = {
  message: string;
  card: AIAgentReplyCard | null;
};

export type CallSummary =
  | {
      uuid: string;
      status: typeof CALL_SUMMARY_STATUS.SUMMARY_GENERATED;
      summary: string;
      is_success: true;
    }
  | {
      uuid: string;
      status: CallSummaryStatus;
      summary: string | null;
      is_success: false;
    };

export type CallRecording = {
  uuid: string;
  recording_sid: string;
  recording_url: string;
  duration: number;
  status: string;
  call_summary: CallSummary | null;
};

export type CallHistoryResponse = {
  total_pages: number;
  total_count: number;
  current_page: number;
  per_page: number;
  calls: AgentDetails[];
};

type AllSharedMessageResponse = {
  messages_forwarded_on_whatsapp: MessageUIComponentProps["message"][];
  forwarded_messages_count: number;
};

export type TicketDetails = {
  ticket_uuid: string;
  message_id: string;
  resolved: boolean;
  ticket_name: string;
  ticket_title: string;
  is_internal_ticket: boolean;
};

export type MessageAttachmentFromDocument = {
  file_id: string;
  document_path: string;
  parent_file_id: string;
  title: string;
  mime_type: string;
};

export type ThreadFeedbackRatingOption = keyof typeof THREAD_FEEDBACK_RATING;

export const chatApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    createMessageDraft: build.mutation<
      { message_draft: string },
      { channelUrl: string; message: string | null }
    >({
      query: ({ channelUrl, message }) => ({
        url: `/api/inkle/messaging/channel/${channelUrl}/message_draft/`,
        body: {
          message_draft: message,
        },
        method: "POST",
      }),
    }),
    getDraftMessage: build.query<
      { message_draft: string },
      { channelUrl: string }
    >({
      query: ({ channelUrl }) => ({
        url: `/api/inkle/messaging/channel/${channelUrl}/message_draft/`,
      }),
    }),

    getUnresolvedTicketMessageIds: build.query<
      {
        ticket_messages: TicketDetails[];
      },
      {
        channelUrl: string;
        resolved_status?: boolean;
      }
    >({
      query: ({ channelUrl, resolved_status }) => {
        let queryUrl = qs.stringify(
          {
            resolved_status,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/tasks/channel/${channelUrl}/ticket-messages/${queryUrl}`,
        };
      },
      providesTags: ["ChatTickets"],
    }),

    getListOfAllPhoneCalls: build.query<
      CallHistoryResponse,
      {
        pageNum: number;
        agents: string | null;
        callActivity: string | null;
        endDate: string | null;
        groups: string | null;
        startDate: string | null;
        searchQuery: string | null;
      }
    >({
      query: ({
        pageNum,
        agents,
        callActivity,
        endDate,
        groups,
        startDate,
        searchQuery,
      }) => {
        let queryUrl = qs.stringify(
          {
            page_num: pageNum,
            agent_profile_id: agents,
            call_type: callActivity,
            to_date: endDate,
            company_group_id: groups,
            from_date: startDate,
            search_term: searchQuery,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/company/v2/phone_call/get/${queryUrl}`,
        };
      },
      providesTags: ["CALL_HISTORY"],
    }),

    makeCustomerPhoneCall: build.mutation<AgentDetails, { groupId: string }>({
      query: ({ groupId }) => {
        return {
          url: `/api/inkle/company/v2/group/${groupId}/simulring/phone_call/`,
          method: "POST",
        };
      },
    }),
    toggleChannelReplied: build.mutation<
      { channel_url: string; needs_reply: boolean },
      { channelId: string; channelNeedsReply: boolean }
    >({
      query: ({ channelId, channelNeedsReply }) => {
        return {
          url: `/api/inkle/messaging/channel/${channelId}/reply_status/`,
          method: "POST",
          body: {
            channel_needs_reply: channelNeedsReply,
          },
        };
      },
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(triggerChannelListRefetch());
      },
    }),
    toggleChannelRepliedForBulk: build.mutation<
      void,
      { channelIds: string[]; channelNeedsReply: boolean }
    >({
      query: ({ channelIds, channelNeedsReply }) => {
        return {
          url: `/api/inkle/messaging/channels/reply_status/`,
          method: "POST",
          body: {
            channel_urls: channelIds,
            channel_needs_reply: channelNeedsReply,
          },
        };
      },
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(triggerChannelListRefetch());
      },
    }),
    sendAnnouncement: build.mutation({
      query: ({ message, group_ids }) => {
        return {
          url: `/api/inkle/messaging/announcements/`,
          method: "POST",
          body: { message, group_ids },
        };
      },
    }),
    deleteMessage: build.mutation<
      any,
      { channel_id: string; message_id: string }
    >({
      query: ({ channel_id, message_id }) => {
        return {
          url: `/api/inkle/messaging/channel/${channel_id}/message/${message_id}/`,
          method: "DELETE",
        };
      },
    }),
    getAnnouncementsGroups: build.query<
      {
        group_name: string;
        group_uuid: string;
      }[],
      {
        searchTerm?: string | null;
        taskTemplateIds?: string | null;
        taskStatusIds?: string | null;
        seasons?: string | null;
      }
    >({
      query: ({ searchTerm, taskTemplateIds, taskStatusIds, seasons }) => {
        let queryUrl = qs.stringify(
          {
            search_term: searchTerm,
            task_template_ids: taskTemplateIds,
            task_status_ids: taskStatusIds,
            seasons: seasons,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/messaging/announcements/${queryUrl}`,
        };
      },
    }),

    forwardMessage: build.mutation<
      void,
      { channelIds: string[]; messageId: string; message?: string }
    >({
      query: ({ messageId, channelIds, message }) => {
        return {
          url: `/api/inkle/messaging/message/${messageId}/`,
          method: "POST",
          body: {
            channel_ids: channelIds,
            message_type: "FORWARD",
            text: message,
          },
        };
      },
    }),

    getMembersCanBeAddedToChannel: build.query<
      {
        name: string;
        profile_url: string | null;
        email: string;
        member_uuid: string;
        member_id: string;
      }[],
      { channelId: string; groupId: string }
    >({
      query: ({ channelId, groupId }) => {
        return {
          url: `/api/inkle/messaging/group/${groupId}/channel/${channelId}/possible-members/`,
        };
      },
    }),

    addMemberToChannel: build.mutation<
      void,
      { channelId: string; memberId: string; groupId: string }
    >({
      query: ({ channelId, memberId, groupId }) => {
        return {
          url: `/api/inkle/messaging/group/${groupId}/channel/${channelId}/member/${memberId}/invite/`,
          method: "POST",
        };
      },
    }),

    getAllChannelMember: build.query<
      {
        name: string;
        profile_url: string | null;
        email: string;
        member_uuid: string;
        mobile: string | null;
        profile_id: string;
        whatsapp_availability: {
          mobile: string;
          available: boolean;
        }[];
        slack_access: boolean;
        is_archived_user: boolean;
      }[],
      { channelId: string; groupId: string }
    >({
      query: ({ channelId, groupId }) => {
        return {
          url: `/api/inkle/messaging/group/${groupId}/channel/${channelId}/members/`,
        };
      },
    }),
    getMessageDetails: build.query<
      {
        forwarded_on_whatsapp: {
          forwarded_by: {
            name: string;
            email: string;
          };
          forwarded_to: {
            name: string;
            email: string;
            to_mobile: string;
            profile_url?: string;
          };
          delivery_status: string;
          time_stamp: Date;
        }[];
        forwarded_on_email: {
          forwarded_by: {
            name: string;
            email: string;
          };
          forwarded_to: {
            name: string;
            email: string;
            profile_url?: string;
          };
          delivery_status: string;
          time_stamp: Date;
        }[];
        forwarded_on_slack: [
          {
            forwarded_to: {
              name: string;
              email: string;
              profile_url: string;
            };
            delivery_status: string;
            time_stamp: Date;
          }
        ];
      },
      {
        channelId: string;
        groupId: string;
        messageId: string;
      }
    >({
      query: ({ channelId, groupId, messageId }) => {
        return {
          url: `/api/inkle/messaging/group/${groupId}/channel/${channelId}/message/${messageId}/details/`,
        };
      },
      providesTags: ["MESSAGE_DETAILS"],
    }),

    forwardMessageToWhatsapp: build.mutation<
      void,
      {
        channelId: string;
        messageId: string;
        groupId: string;
        payload: {
          text: string;
          to_users: {
            email: string;
            mobile: string;
          }[];
        };
      }
    >({
      query: ({ channelId, messageId, groupId, payload }) => {
        return {
          url: `/api/inkle/messaging/group/${groupId}/channel/${channelId}/message/${messageId}/forward-to-whatsapp/`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ["MESSAGE_DETAILS", "ALL_SHARED_MESSAGE"],
    }),

    getAllSharedMessage: build.query<
      AllSharedMessageResponse,
      { channelId: string; groupId: string }
    >({
      query: ({ channelId, groupId }) => {
        return {
          url: `/api/inkle/messaging/group/${groupId}/channel/${channelId}/details`,
        };
      },
      providesTags: ["ALL_SHARED_MESSAGE"],
    }),
    makePhoneCallFromCRM: build.mutation<
      AgentDetails,
      {
        groupUserId: string;
        mobile: string;
        channelId?: string;
        companyGroupId?: string;
      }
    >({
      query: ({ groupUserId, mobile, channelId, companyGroupId }) => {
        return {
          url: `/api/inkle/serviceteams/call/`,
          method: "POST",
          body: {
            group_user_id: groupUserId,
            mobile_number: mobile,
            channel_url: channelId,
            company_group_id: companyGroupId,
          },
        };
      },
      invalidatesTags: ["CALL_HISTORY"],
    }),
    getChatSLAMetrics: build.query<
      {
        average_chat_response_time: {
          hours: number;
          minutes: number;
        };
      },
      { startDate?: string; endDate?: string; channelUrl?: string }
    >({
      query: ({ startDate, endDate, channelUrl }) => {
        let queryUrl = qs.stringify(
          {
            start_date: startDate,
            end_date: endDate,
            channel_url: channelUrl,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/messaging/sla_metrics/${queryUrl}`,
        };
      },
    }),

    getCallDetails: build.query<
      {
        notes: string;
        call_status: AdminCallCardStatusType;
        recordings: CallRecording[];
      },
      { groupUserPhoneCallId?: string }
    >({
      query: ({ groupUserPhoneCallId }) => {
        return {
          url: `/api/inkle/serviceteams/call/${groupUserPhoneCallId}/`,
        };
      },
      providesTags: (result) => (result ? ["CALL_DETAILS"] : []),
    }),

    editCallDetails: build.mutation<
      { notes: string },
      { groupUserPhoneCallId?: string; notes: string; callConnected?: boolean }
    >({
      query: ({ groupUserPhoneCallId, notes, callConnected }) => {
        return {
          url: `/api/inkle/serviceteams/call/${groupUserPhoneCallId}/`,
          method: "PATCH",
          body: { notes, call_connected: callConnected },
        };
      },
      invalidatesTags: (result) => (result ? ["CALL_DETAILS"] : []),
    }),

    getForwardedFromMessage: build.query<
      MessageResponse<DefaultStreamChatGenerics>,
      { channelUrl: string; messageID: string }
    >({
      query: ({ channelUrl, messageID }) => {
        return {
          url: `api/inkle/messaging/channel/${channelUrl}/forwarded_message/${messageID}/`,
        };
      },
    }),

    getTeamsForChannel: build.query<
      { name: string; uuid: string; is_in_channel: boolean }[],
      { channelUrl: string }
    >({
      query: ({ channelUrl }) => {
        return {
          url: `api/inkle/messaging/channel/${channelUrl}/teams/`,
        };
      },
      providesTags: (result) => (result ? ["TEAMS_FOR_CHANNEL"] : []),
    }),

    getMembersOfTeamForChannel: build.query<
      {
        member: {
          name: string;
          profile_url: string | null;
          email: string;
          member_uuid: string;
        };
        member_id: string;
        is_channel_member: boolean;
      }[],
      { channelUrl: string; teamId: string }
    >({
      query: ({ channelUrl, teamId }) => {
        return {
          url: `api/inkle/messaging/channel/${channelUrl}/team/${teamId}/members/`,
        };
      },
      providesTags: (result) => (result ? ["MEMBERS_FOR_CHANNEL"] : []),
    }),

    inviteTeamOrMemberInChannel: build.mutation<
      {
        name: string;
        profile_url: string | null;
        email: string;
        member_uuid: string;
      },
      {
        groupId: string;
        channelUrl: string;
        teamId: string;
        memberIds: string[];
      }
    >({
      query: ({ groupId, channelUrl, teamId, memberIds }) => {
        return {
          url: `api/inkle/messaging/group/${groupId}/channel/${channelUrl}/invite/team-member/`,
          method: "POST",
          body: { team_id: teamId, member_ids: memberIds },
        };
      },
      invalidatesTags: ["MEMBERS_FOR_CHANNEL", "TEAMS_FOR_CHANNEL"],
    }),

    generateResponseForMessage: build.mutation<
      { message: string; channel: string },
      { message: string; channelId: string }
    >({
      query: ({ message, channelId }) => {
        return {
          url: `/api/inkle/messaging/channel/${channelId}/generate/`,
          method: "POST",
          body: { message },
        };
      },
      invalidatesTags: (result) =>
        result
          ? [
              { id: result.channel, type: "AI_RESPONSE" },
              { id: "LIST", type: "AI_RESPONSE" },
            ]
          : [],
    }),

    strengthenMessage: build.mutation<
      { strengthened_message: string },
      { message: string }
    >({
      query: ({ message }) => ({
        url: `/api/inkle/messaging/strengthen-message/`,
        method: "POST",
        body: { message },
      }),
    }),

    getLatestAIGeneratedMessage: build.query<
      { message: string; uuid: string; channel: string; is_archived: boolean },
      { channelId: string }
    >({
      query: ({ channelId }) => {
        return {
          url: `/api/inkle/messaging/channel/${channelId}/generate/`,
        };
      },
      providesTags: (result) =>
        result
          ? [{ id: result.channel, type: "AI_RESPONSE" }]
          : [{ id: "LIST", type: "AI_RESPONSE" }],
    }),

    updateAIGeneratedMessage: build.mutation<
      { message: string; channel: string },
      {
        responseId: string;
        channelId: string;
        archived?: boolean;
        messageSent?: string;
      }
    >({
      query: ({ responseId, channelId, messageSent, archived }) => {
        return {
          url: `/api/inkle/messaging/channel/${channelId}/generate/${responseId}/`,
          method: "PUT",
          body: { is_archived: archived, message_sent: messageSent },
        };
      },
      invalidatesTags: (result) =>
        result ? [{ id: result.channel, type: "AI_RESPONSE" }] : [],
    }),

    toggleChannelFrozen: build.mutation<void, { channelId: string }>({
      query: ({ channelId }) => {
        return {
          url: `/api/inkle/messaging/channel/${channelId}/freeze/`,
          method: "POST",
        };
      },
    }),
    callAccountManager: build.mutation<
      AgentDetails,
      { groupId: string; agentId: string }
    >({
      query: ({ groupId, agentId }) => {
        return {
          url: `/api/inkle/company/v2/group/${groupId}/agent_phone_call/${agentId}/`,
          method: "POST",
        };
      },
      invalidatesTags: ["CALL_HISTORY"],
    }),

    getMessageEmailDetails: build.query<
      MessageEmailDetailsType[],
      {
        channelId: string;
      }
    >({
      query: ({ channelId }) => {
        return {
          url: `/api/inkle/messaging/channel/${channelId}/email-messages/`,
        };
      },
      providesTags: ["MESSAGE_EMAIL"],
    }),

    getChannelEmailSuppressionStatus: build.query<
      EmailSuppressionStatus[],
      {
        channelId: string;
      }
    >({
      query: ({ channelId }) => {
        return {
          url: `/api/inkle/messaging/channel/${channelId}/email-suppression-status/`,
        };
      },
      providesTags: ["EMAIL_SUPPRESSION"],
    }),

    reactivateChannelEmailSuppressions: build.mutation<
      {
        reactivated: string[];
        failed: { email: string; message: string | null }[];
      },
      {
        channelId: string;
        emails: string[];
      }
    >({
      query: ({ channelId, emails }) => {
        return {
          url: `/api/inkle/messaging/channel/${channelId}/email-suppression-status/`,
          method: "PUT",
          body: { emails },
        };
      },
      invalidatesTags: ["EMAIL_SUPPRESSION"],
    }),

    sendMessageToEmail: build.mutation<
      void,
      {
        channelId: string;
        messageId: string;
        groupId: string;
        payload: {
          email_subject: string;
          email_text: string;
          message_type: string;
          to_users: {
            email: string;
            mobile: string;
          }[];
        };
      }
    >({
      query: ({ channelId, messageId, groupId, payload }) => {
        return {
          url: `/api/inkle/messaging/group/${groupId}/channel/${channelId}/message/${messageId}/forward-to-email/
`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: [
        "MESSAGE_DETAILS",
        "MESSAGE_EMAIL",
        "EMAIL_SUPPRESSION",
      ],
    }),
    sendMessageToSlack: build.mutation<
      void,
      {
        channelId: string;
        messageId: string;
        groupId: string;
        payload: {
          to_users: {
            email: string;
          }[];
        };
      }
    >({
      query: ({ channelId, messageId, groupId, payload }) => {
        return {
          url: `/api/inkle/messaging/group/${groupId}/channel/${channelId}/message/${messageId}/forward-to-slack/
`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ["MESSAGE_DETAILS"],
    }),
    updateChatActionItemStatus: build.mutation<
      void,
      {
        messageId: string;
        groupId: string;
        payload: {
          custom_type: string;
          card_status_str: "DEPRECATED" | "REQUESTED";
        };
      }
    >({
      query: ({ messageId, groupId, payload }) => {
        return {
          url: `/api/inkle/messaging/group/${groupId}/message/${messageId}/card_status/`,
          method: "PATCH",
          body: payload,
        };
      },
      invalidatesTags: ["ALL_ACTION_ITEMS", "MESSAGE_DETAILS"],
    }),
    quickReplyToMessage: build.mutation<
      { messageId: string; channelId: string },
      {
        payload: {
          message_id: string;
          task_id?: string;
          selected_option: string;
        };
        channelId: string;
      }
    >({
      query: ({ payload, channelId }) => ({
        url: `/api/inkle/messaging/channel/${channelId}/trigger-acr-message/acr-reply/`,
        method: "POST",
        body: payload,
      }),
    }),

    notifyCart: build.mutation<void, { entityId: string }>({
      query: ({ entityId }) => ({
        url: `/api/inkle/messaging/entity/${entityId}/trigger-acr-message/`,
        method: "POST",
      }),
    }),
    generateAIResponse: build.mutation<
      { messageId: string; channelId: string },
      {
        messageId: string;
        channelId: string;
      }
    >({
      query: ({ channelId, messageId }) => ({
        url: `/api/inkle/ai_agents/channels/${channelId}/messages/${messageId}/respond/`,
        method: "POST",
      }),
    }),
    giveAIResponseFeedback: build.mutation<
      { messageId: string; channelId: string },
      {
        messageId: string;
        channelId: string;
        body: {
          thumbs_rating?: 1 | -1;
          user_feedback?: string;
        };
      }
    >({
      query: ({ channelId, messageId, body }) => ({
        url: `/api/inkle/ai_agents/channels/${channelId}/messages/${messageId}/rating/`,
        method: "PUT",
        body,
      }),
    }),
    getMessageAttachments: build.query<
      MessageAttachmentFromDocument[],
      { messageId: string }
    >({
      query: ({ messageId }) => {
        return {
          url: `/api/inkle/messaging/message/${messageId}/attachments/`,
        };
      },
    }),
    moveChatFiles: build.mutation<
      fileAndFolder,
      {
        groupId: string;
        destination_folder_id: string;
        messageId: string;
        attachment_indexes: number[];
      }
    >({
      query: ({ groupId, messageId, ...payload }) => {
        return {
          url: `/api/inkle/documents/group/${groupId}/message/${messageId}/files/move/`,
          method: "POST",
          body: payload,
        };
      },
    }),

    aiThreadFeedback: build.mutation<
      null,
      {
        channelId: string;
        messageId: string;
        threadFeedbackRating: ThreadFeedbackRatingOption;
        threadFeedbackText: string;
      }
    >({
      query: ({
        channelId,
        messageId,
        threadFeedbackRating,
        threadFeedbackText,
      }) => {
        return {
          url: `/api/inkle/ai_agents/channels/${channelId}/messages/${messageId}/thread-feedback/`,
          method: "PUT",
          body: {
            thread_feedback_rating: threadFeedbackRating,
            thread_feedback_text: threadFeedbackText,
          },
        };
      },
    }),

    getFilteredChannelIds: build.query<
      { channel_ids: string[] },
      {
        groupId: string;
        entityIds?: string[];
        seasons?: string[];
        frozen?: boolean;
      }
    >({
      query: ({ groupId, entityIds, seasons, frozen }) => ({
        url: `/api/inkle/messaging/group/${groupId}/filtered-channels/`,
        params: {
          ...(entityIds?.length ? { entity_ids: entityIds.join(",") } : {}),
          ...(seasons?.length ? { seasons: seasons.join(",") } : {}),
          frozen: Boolean(frozen),
        },
      }),
    }),

    startAIAgent: build.mutation<
      AIAgentReply,
      {
        channelUrl: string;
        outcomeName: string;
        groupId: string;
        entityId?: string;
        userQuery?: string;
      }
    >({
      query: ({ channelUrl, outcomeName, groupId, entityId, userQuery }) => ({
        url: `/api/inkle/messaging/channel/${channelUrl}/agent/start/`,
        method: "POST",
        body: {
          outcome_name: outcomeName,
          group_id: groupId,
          entity_id: entityId ?? "",
          user_query: userQuery ?? "",
        },
      }),
    }),
  }),

  overrideExisting: false,
});

export const {
  useCreateMessageDraftMutation,
  useGetDraftMessageQuery,
  useGetUnresolvedTicketMessageIdsQuery,
  useGetListOfAllPhoneCallsQuery,
  useMakeCustomerPhoneCallMutation,
  useToggleChannelRepliedMutation,
  useDeleteMessageMutation,
  useGetAnnouncementsGroupsQuery,
  useSendAnnouncementMutation,
  useForwardMessageMutation,
  useGetMembersCanBeAddedToChannelQuery,
  useAddMemberToChannelMutation,
  useGetAllChannelMemberQuery,
  useForwardMessageToWhatsappMutation,
  useGetMessageDetailsQuery,
  useGetAllSharedMessageQuery,
  useMakePhoneCallFromCRMMutation,
  useGetChatSLAMetricsQuery,
  useGetCallDetailsQuery,
  useEditCallDetailsMutation,
  useLazyGetForwardedFromMessageQuery,
  useGetTeamsForChannelQuery,
  useGetMembersOfTeamForChannelQuery,
  useInviteTeamOrMemberInChannelMutation,
  useGenerateResponseForMessageMutation,
  useToggleChannelRepliedForBulkMutation,
  useToggleChannelFrozenMutation,
  useSendMessageToEmailMutation,
  useGetMessageEmailDetailsQuery,
  useGetChannelEmailSuppressionStatusQuery,
  useReactivateChannelEmailSuppressionsMutation,
  useGetLatestAIGeneratedMessageQuery,
  useUpdateAIGeneratedMessageMutation,
  useUpdateChatActionItemStatusMutation,
  useCallAccountManagerMutation,
  useQuickReplyToMessageMutation,
  useNotifyCartMutation,
  useSendMessageToSlackMutation,
  useGenerateAIResponseMutation,
  useGiveAIResponseFeedbackMutation,
  useGetMessageAttachmentsQuery,
  useMoveChatFilesMutation,
  useAiThreadFeedbackMutation,
  useStrengthenMessageMutation,
  useStartAIAgentMutation,
  useGetFilteredChannelIdsQuery,
} = chatApi;
